/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import 'lib/bootstrap-grid-namespaced.scss'
import StoreFunctions from 'stores/storeFunctions'
import { DiamondsSearchPage } from '@unbridaled/search-sdk'
import { Preloader } from 'lib/components/preloader'
import { ErrorModal } from 'lib/components/modals/ErrorModal'
import { ConfirmationModal } from 'lib/components/modals/ConfirmationModal'
import { DASOrderSuccessfullModal } from 'lib/components/modals/DASOrderSuccessfullModal'
import { BookmarksModal } from 'lib/components/modals/BookmarksModal'
import { LoginModal } from 'lib/components/modals/LoginModal'
import { JourneyBar } from 'lib/components/journeyBar'
import { useWishlist } from 'utils/use-wishlist'
import { useSearchParamsProps } from 'utils/use-search-params-props'
import { truncateCurrencyAmount } from 'constants/currencies'
import { useMediaBreakpoint } from '@unbridaled/react-hooks'
import { Button } from '@unbridaled/ui-components/Button'
import { ArrowHeadDownIcon } from '@unbridaled/icons/ArrowHeadDownIcon'
import { LogoutIcon } from '@unbridaled/icons/LogoutIcon'
import { Menu, MenuButton, MenuItems, MenuLabel } from '@unbridaled/ui-components/Menu'
import { Roles, UserRoles } from 'constants/roles'
import { JewelryType } from 'constants/jewelryTypes'
import logger from 'utils/logger'
import { Config, DiamondsMainSearchPageState, DiamondSearchProps } from 'types/interfaces'
import classNames from 'classnames'
import { useAppContext } from 'lib/context/AppContext'
import SimpleStore from 'stores/simpleStore'
import { getSearchSdkLocale, serverErrorMessage } from 'i18n'
import { getLoginModalLabels } from 'lib/components/modals/loginModalLabels'

const v360PlayerUrl = `${process.env.VIEWER_URL}/api/360player`
// const nativeV360PlayerUrl = `${location.origin}/apps/ub/360player`

declare global {
  interface Window {
    Shopify: {
      currency: {
        active: string
        rate: number
      }
    }
  }
}

type SelectDiamondButtonProps = {
  data: { [key: string]: any }
  onSelect: (data: { [key: string]: any }) => void
}

type StableSelectDiamondButtonProps = {
  data: { [key: string]: any }
  onSelect: (data: { [key: string]: any }) => void
}

const StableSelectDiamondButton: React.FC<StableSelectDiamondButtonProps> = ({
  data,
  onSelect
}) => {
  const stableDataRef = React.useRef<{ [key: string]: any } | null>(null)

  const shouldUpdate =
    !stableDataRef.current ||
    stableDataRef.current.sku !== data?.sku

  if (shouldUpdate) {
    stableDataRef.current = data
  }

  if (!stableDataRef.current) {
    return null
  }

  return (
    <SelectDiamondButtonMemo
      data={stableDataRef.current}
      onSelect={onSelect}
    />
  )
}

const AddingSkuContext = React.createContext<string | null>(null)

const SelectDiamondButtonMemo = React.memo<SelectDiamondButtonProps>(
  ({ data, onSelect }) => {
    const addingSku = React.useContext(AddingSkuContext)
    const loading = addingSku === data?.sku
    return (
      <Button
        size="sm"
        variant="dark"
        fullWidth
        loading={loading}
        className="uui-rounded-t-none uui-h-[38px]"
        onClick={e => {
          e.stopPropagation()
          if (!loading) onSelect(data)
        }}
      >
        SELECT DIAMOND
      </Button>
    )
  },
  (prev, next) => {
    return (
      prev.data?.sku === next.data?.sku &&
      prev.onSelect === next.onSelect
    )
  }
)


SelectDiamondButtonMemo.displayName = 'SelectDiamondButtonMemo'

function parseURLFilters(): Record<string, any> {
  try {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const queryString = urlSearchParams.get('filters');
    if (queryString !== null && queryString !== '') {
      return JSON.parse(atob(queryString));
    }
  } catch (e) {
    console.error("Failed to parse search filters from URL:", e);
  }
  return {};
}

export const DiamondsMainSearchPage: React.FC<any> = props => {
  const { t } = useTranslation()
  const [nativeV360PlayerUrl] = useState(() => `${location.origin}/apps/ub/360player`)
  const { diamondStore, appConfig, enabledDiamondGemTypes, typeLabels, currencyInfo } = useAppContext()
  const storeFunctions = useMemo(() => new StoreFunctions(), [])
  const simpleStore = useMemo(() => new SimpleStore(), [])
  const history = useHistory()
  const { getDefaultParams, onSelectedDiamondChange } = useSearchParamsProps()
  const USER_COOKIE_NAME = 'UNBRIDALED_USER'
  const [userData, setUserData] = useState(storeFunctions.getUser());
  const [role, setRole] = useState(storeFunctions.getUserRole())
  const [isMemoMode, setIsMemoMode] = useState(storeFunctions.isMemoMode())
  const [loaded, setLoaded] = useState(false)
  const [shopName, setShopName] = useState(null)
  const [shopCurrency, setShopCurrency] = useState<string | null>(null)
  const [config] = useState<Config>({
    sourceTypes: appConfig?.source_types,
    advancedSearchCartEnabled: appConfig?.advanced_search_cart_enabled,
    ringSizeRegion: appConfig?.ring_size_region
  })
  const [selectedGems, setSelectedGems] = useState<DiamondsMainSearchPageState['selectedGems']>([])
  const [selectedGemsOrderType, setSelectedGemsOrderType] = useState('i')
  const [addingSku, setAddingSku] = useState<string | null>(null)

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false)
  const [dasOrderSuccessfullModalOpen, setDasOrderSuccessfullModalOpen] = useState(false)
  const [bookmarksModalOpen, setBookmarksModalOpen] = useState(false)

  const [errorModalOpen, setErrorModalOpen] = useState(false)
  const [error, setError] = useState<DiamondsMainSearchPageState['error'] | null>(null)

  const [searchFiltersInit] = useState(parseURLFilters)
  const searchFiltersRef = useRef<Record<string, any>>(searchFiltersInit)
  // True after the user cleared all filters (SDK reset). The next search must
  // match the default one made on first open: no product overrides, default limit.
  const filtersClearedRef = useRef(false)
  const paramsChangedOnceRef = useRef(false)
  const locationSearchRef = useRef(props.location.search)

  useEffect(() => {
    locationSearchRef.current = props.location.search
  }, [props.location.search])

  const [loginModalConfig, setLoginModalConfig] = useState({
    open: false,
    loadingOTP: false,
    mode: 'email',
    value: '',
    methodId: '',
    email: '',
    error: null as string | null
  })
  const isMobile = !useMediaBreakpoint('sm')

  const wishlistProps = useWishlist('diamond')

  const [overrides, setOverrides] = useState<{ [key: string]: any }>({})

  useEffect(() => {
    const params = new URLSearchParams(props.location.search);
    const encodedOverrides = params.get('overrides');
    if (!encodedOverrides) return;
    try {
      const decoded = JSON.parse(atob(encodedOverrides));
      setOverrides(decoded);
    } catch (error) {
      console.error("Failed to decode overrides:", error);
      setOverrides({});
    }
  }, []);

  const [productData, setProductData] = useState<{ variantId: string | null; ringSize: string | null; variants: any[]; shapes: any[] }>({ variantId: null, ringSize: null, variants: [], shapes: [] })
  const inFlightSearchRequestsRef = useRef<Map<string, Promise<any>>>(new Map())
  const searchPayloadCacheRef = useRef<Map<string, any>>(new Map())

  const productDataRef = useRef(productData)
  useEffect(() => {
    productDataRef.current = productData
  }, [productData])

  useEffect(() => {
    const fetchProductData = async () => {
      const params = new URLSearchParams(props.location.search)
      const variantId = params.get('productId')
      if (variantId) {
        const variant = await storeFunctions.getVariantById(variantId)
        if (variant?.product?.id) {
          const productId = variant.product.id.match(/\d+/)[0]
          const productData = await storeFunctions.getShopifyProductData(productId)
          setProductData({ variantId, ringSize: params.get('ringSize'), variants: productData?.variants || [], shapes: productData?.shapes || [] })
        }
      }
    }
    fetchProductData()
  }, [])

  const prepareSearchParams = useCallback(
    (params: any) => {
      const urlSearchParams = new URLSearchParams(window.location.search)
      const overridesParams = urlSearchParams.get('overrides')
      let searchParams = params
      if (filtersClearedRef.current) {
        // SDK reset uses its own limit (50); use the same default as the initial open.
        const defaultLimit = getDefaultParams('')?.limit
        if (typeof defaultLimit === 'number') {
          searchParams = { ...params, offset: 0, limit: defaultLimit }
        }
      } else if (overridesParams) {
        const overrides = JSON.parse(atob(overridesParams))
        searchParams = storeFunctions.mergeOverridesIntoFilters(
          params,
          overrides,
          searchFiltersRef
        )
      }
      // Always request DAS in the shop's merchant (base) currency for every
      // role/mode, so retail_price comes back in the merchant currency the
      // Shopify variant is minted from (e.g. AUD) — not USD. Display then applies
      // the Shopify presentment rate to the customer currency. Sending 'USD' for
      // merchant/memo returned USD prices mislabeled as the shop currency.
      searchParams.currency_code = shopCurrency || currencyInfo.currency
      searchParams.include_total = true
      return searchParams
    },
    [shopCurrency, currencyInfo.currency, storeFunctions]
  )

  const normalizeRetailPrice = useCallback((value: unknown): number => {
    const parsed = typeof value === 'string' ? Number(value.replace(/[\,]/g, '')) : Number(value)
    if (!Number.isFinite(parsed)) return 0
    return Math.round(parsed * 100) / 100
  }, [])

  const mapDiamondsForDisplay = useCallback(
    (gems: any[] = []): any[] => {
      const isRegularMode = role !== Roles.Merchant && !isMemoMode
      const shopifyRate =
        shopCurrency && currencyInfo.currency !== shopCurrency
          ? window.Shopify?.currency?.rate || 1
          : 1

      return gems.map(gem => {
        const storeRetailPrice = normalizeRetailPrice(gem?.retail_price)
        if (isRegularMode) {
          const converted = truncateCurrencyAmount(storeRetailPrice * shopifyRate)
          console.log(
            `[price][diamonds-main-search] ${shopCurrency || 'base'} ${storeRetailPrice} -> ${currencyInfo.currency} ${converted} @ Shopify rate ${shopifyRate}`
          )
          return {
            ...gem,
            retail_price: converted,
            currency_code: currencyInfo.currency
          }
        }

        // Merchant/memo (double) mode: keep retail_price in the shop currency for
        // the primary line; the customer-currency (second) line is retail_price x
        // the Shopify presentment rate, matching the other surfaces (not DAS's rate).
        const cartConverted = truncateCurrencyAmount(storeRetailPrice * shopifyRate)
        console.log(
          `[price][diamonds-main-search][double] ${shopCurrency || 'base'} ${storeRetailPrice} -> ${currencyInfo.currency} ${cartConverted} @ Shopify rate ${shopifyRate}`
        )
        return {
          ...gem,
          retail_price: storeRetailPrice,
          cart_price: cartConverted,
          currency_code: shopCurrency || gem?.currency_code || currencyInfo.currency
        }
      })
    },
    [role, isMemoMode, shopCurrency, currencyInfo.currency, normalizeRetailPrice]
  )

  const buildSearchRequestKey = useCallback((searchParams: any): string => {
    try {
      return JSON.stringify(searchParams)
    } catch (_e) {
      return JSON.stringify(searchParams)
    }
  }, [])

  const fetchDiamondsPayload = useCallback(
    async (params: any, signal?: AbortSignal) => {
      const searchParams = prepareSearchParams(params)
      const cacheKey = buildSearchRequestKey(searchParams)

      const cachedPayload = searchPayloadCacheRef.current.get(cacheKey)
      if (cachedPayload) {
        return { payload: cachedPayload, searchParams }
      }

      const existingRequest = inFlightSearchRequestsRef.current.get(cacheKey)
      if (existingRequest) {
        const payload = await existingRequest
        return { payload, searchParams }
      }

      const requestPromise = diamondStore
        .runFilter(searchParams, 'diamond_search', signal)
        .then((payload: any) => {
          const mappedPayload = payload
            ? {
                ...payload,
                gems: mapDiamondsForDisplay(payload?.gems || [])
              }
            : payload
          if (searchPayloadCacheRef.current.size > 30) {
            searchPayloadCacheRef.current.clear()
          }
          searchPayloadCacheRef.current.set(cacheKey, mappedPayload)
          return mappedPayload
        })
        .finally(() => {
          // Guard against deleting a newer request's entry: the abort listener
          // may have already cleared this key and a subsequent call may have
          // inserted its own promise before this microtask runs.
          if (inFlightSearchRequestsRef.current.get(cacheKey) === requestPromise) {
            inFlightSearchRequestsRef.current.delete(cacheKey)
          }
        })

      inFlightSearchRequestsRef.current.set(cacheKey, requestPromise)

      // Remove immediately on abort so subsequent calls with a fresh signal
      // don't await the aborting promise and silently discard a valid response.
      if (signal) {
        const removeEntry = () => {
          if (inFlightSearchRequestsRef.current.get(cacheKey) === requestPromise) {
            inFlightSearchRequestsRef.current.delete(cacheKey)
          }
        }
        if (signal.aborted) {
          removeEntry()
        } else {
          signal.addEventListener('abort', removeEntry, { once: true })
        }
      }
      const payload = await requestPromise
      return { payload, searchParams }
    },
    [diamondStore, prepareSearchParams, buildSearchRequestKey, mapDiamondsForDisplay]
  )

  const getDiamonds = useCallback(
    async (params: any, signal?: AbortSignal) => {
      try {
        const { payload: diamondsData, searchParams } = await fetchDiamondsPayload(params, signal)
        if (diamondsData && diamondsData.metadata) {
          return {
            offset: searchParams.offset || 0,
            total: diamondsData.metadata.total_count,
            data: diamondsData.gems,
            limit: diamondsData.metadata.limit
          }
        }
        return null
      } catch (e) {
        if ((e as Error).name === 'AbortError') {
          throw e
        }
        logger.info(e)
        return null
      }
    },
    [fetchDiamondsPayload]
  )

  const getDiamondsCount = useCallback(
    async (params: any, signal?: AbortSignal): Promise<{ total: number } | null> => {
      try {
        const { payload } = await fetchDiamondsPayload(params, signal)
        return { total: payload?.metadata?.total_count || 0 }
      } catch (e) {
        if ((e as Error).name === 'AbortError') {
          throw e
        }
        logger.info(e)
        return null
      }
    },
    [fetchDiamondsPayload]
  )

  const fetchShopName = async (): Promise<void | null> => {
    try {
      const shopInfo = await diamondStore.getShopName()
      setShopName(shopInfo.shopName)
    } catch (e) {
      logger.error('Failed to get shop name')
      return null
    }
  }

  const fetchShopCurrency = async (): Promise<void | null> => {
    try {
      const currencyData = await simpleStore.ajax('GET', 'shop_currency')
      if (currencyData?.currency) {
        setShopCurrency(currencyData.currency)
        return
      }
      throw new Error('Failed to get shop currency')
    } catch (e) {
      logger.error('Failed to get shop currency')
      return null
    }
  }

  const onLogout = async (): Promise<void> => {
    setRole(UserRoles.MerchantRetail)
    await new Promise(resolve => setTimeout(resolve, 600))
    localStorage.removeItem(USER_COOKIE_NAME)
    setIsMemoMode(false)
    setRole(Roles.Customer)
    wishlistProps?.refresh(Roles.Customer, false)
  }

  const onSwitchToMemoMode = (): void => {
    const userData = JSON.parse(localStorage.getItem(USER_COOKIE_NAME) || '{}')
    localStorage.setItem(USER_COOKIE_NAME, JSON.stringify({
      ...userData,
      user: {
        ...userData.user,
        roles: [UserRoles.MerchantRetail],
        is_owner: false
      }
    }))
    setRole(Roles.Customer)
    setIsMemoMode(true)
    wishlistProps?.refresh(Roles.Customer, true)
  }

  useEffect(() => {
    const loadPageData = async (): Promise<void> => {
      await Promise.all([fetchShopName(), fetchShopCurrency()])
      setLoaded(true)
      wishlistProps?.refresh(Roles.Customer, false)
      const url = new URL(window.location.href);
      if (url.searchParams.get('admin') === 'true') {
        onLogout()
        setLoginModalConfig(prevConfig => ({
          ...prevConfig,
          open: true
        }));
      }
    }

    loadPageData()

    function handleMessage(event: { data: string }): void {
      const certEl = document.querySelector('[class^=Certificate_module_close]')
      const mediaEl = document.querySelector('[class^=MediaPlayer_module_close]')
      if (event.data === 'ubCertificateViewerClosed' && certEl !== null) {
        (certEl as HTMLElement).click()
      } else if (event.data === 'ubMediaViewerClosed' && mediaEl !== null) {
        (mediaEl as HTMLElement).click()
      }
    }
    window.addEventListener('message', handleMessage, false)

    const closeViewer = (e: { key: string }): void => {
      if (e.key === 'Escape') {
        let moduleClass: string | null = null
        moduleClass = '_module_close'
        const element = document.querySelector('[class*=' + moduleClass + ']')
        if (element !== null) {
          (element as HTMLElement).click()
        }
      }
    }
    window.addEventListener('keyup', closeViewer)
    return () => {
      window.removeEventListener('message', handleMessage)
      window.removeEventListener('keyup', closeViewer)
    }
  }, [])

  function findVariantIdByShapeChange(
    variants: any[],
    prevOptions: any[],
    newShape: string | null
  ): string | null {
    const targetOptions = prevOptions.map(opt =>
      opt.name.toLowerCase() === 'shape' ? { ...opt, value: newShape } : opt
    )
    for (const variant of variants) {
      const matches = targetOptions.every(targetOpt => {
        return variant.selected_options.some(
          (vOpt: any) =>
            vOpt.name.toLowerCase() === targetOpt.name.toLowerCase() &&
            vOpt.value.toLowerCase() === targetOpt.value.toLowerCase()
        )
      })
      if (matches) {
        return variant.variant_id
      }
    }

    return null
  }

  const onParamsChange = useCallback(
    (filters: { [key: string]: any }): void => {
      const prevFilters = searchFiltersRef.current || {}
      const nextFilters = { ...filters }

      let needReload = false
      let productVariantChanged = false
      let newSingle: string | null = null

      const productData = productDataRef.current

      if (productData?.variantId && productData?.variants?.length > 0) {
        const normalizeShapes = (arr: any): string[] =>
          (Array.isArray(arr) ? arr : arr ? [arr] : []).map((s: string) =>
            s.includes(':') ? (s.split(':')[1] ?? s) : s
          )

        const prevShapes = normalizeShapes(prevFilters.cut_code_shape)
        const newShapes = normalizeShapes(nextFilters.cut_code_shape)

        const uniquePrev = [...new Set(prevShapes)]
        const uniqueNew = [...new Set(newShapes)]

        const setsEqual = (a: string[], b: string[]) => {
          const sa = new Set(a)
          const sb = new Set(b)
          if (sa.size !== sb.size) return false
          for (const v of sa) if (!sb.has(v)) return false
          return true
        }

        if (!setsEqual(uniquePrev, uniqueNew)) {
          if (uniqueNew.length === 0 || uniqueNew.length > 1) {
            nextFilters.ratio_min = ''
            nextFilters.ratio_max = ''
            needReload = true
          } else {
            const prevSingle = uniquePrev.length === 1 ? uniquePrev[0] : null
            newSingle = uniqueNew[0] ?? null

            if (prevSingle !== newSingle) {
              productVariantChanged = true
              needReload = true

              const shape = productData.shapes.find((s: any) => s.shape === newSingle)
              if (shape && shape.ratio_enabled) {
                nextFilters.ratio_min = shape.ratio_min
                nextFilters.ratio_max = shape.ratio_max
              } else {
                nextFilters.ratio_min = ''
                nextFilters.ratio_max = ''
              }
            }
          }
        }
      }

      searchFiltersRef.current = nextFilters

      const searchParams = new URLSearchParams(window.location.search)
      const hasOnlyPageAndLimit =
        Object.keys(nextFilters).length === 2 &&
        Object.prototype.hasOwnProperty.call(nextFilters, 'page') &&
        Object.prototype.hasOwnProperty.call(nextFilters, 'limit')
      // The first call is the initial mount, not a user "clear all".
      filtersClearedRef.current = hasOnlyPageAndLimit && paramsChangedOnceRef.current
      paramsChangedOnceRef.current = true
      if (hasOnlyPageAndLimit) {
        searchParams.delete('filters')
        // Filters were explicitly cleared — stop re-merging the product's
        // original seeded constraints (ratio/carat/shape) back in, or the
        // "cleared" search silently stays scoped to the product and can
        // return no results.
        searchParams.delete('overrides')
      } else {
        const queryString = btoa(JSON.stringify(nextFilters))
        searchParams.set('filters', queryString)
      }
      const newSearch = `?${searchParams.toString()}`
      const newUrl = `${window.location.pathname}${newSearch}`
      locationSearchRef.current = newSearch
      window.history.replaceState(null, '', newUrl)

      if (productVariantChanged && newSingle) {
        const currentVariant = productData.variants.find(
          variant => variant.variant_id === productData.variantId
        )
        const currentSelectedOptions = currentVariant?.selected_options || []
        const variantId = findVariantIdByShapeChange(
          productData.variants,
          currentSelectedOptions,
          newSingle
        )
        if (variantId) {
          const url = new URL(window.location.href)
          url.searchParams.set('productId', variantId)
          window.history.replaceState(null, '', url.toString())
        }
      }

      if (needReload) {
        window.location.reload()
      }
    },
    []
  )

  const closeErrorModal = (): void => {
    setError(null)
    setErrorModalOpen(false)
  }

  const closeBookmarksModal = (): void => {
    setBookmarksModalOpen(false)
  }

  const closeConfirmationModal = (): void => {
    setConfirmationModalOpen(false)
  }

  const closeDASOrderSuccessfullModal = (): void => {
    setDasOrderSuccessfullModalOpen(false)
  }

  const getFavorites = (): any[] => {
    const favorites = localStorage.getItem('favorites_diamonds')
    return favorites ? JSON.parse(favorites) : []
  }

  const bookmarksEmail = async (): Promise<void> => {
    const favorites = getFavorites()
    if (!favorites.length) {
      setError({
        title: t('bookmarksModal.noStarredDiamonds'),
        message: t('bookmarksModal.starOneDiamond')
      })
      setErrorModalOpen(true)
      return
    }
    const skus = favorites.map(item => item.sku)
    const link = window.location.origin + '/apps/ub/curated_diamonds/' + btoa(skus.join(','))

    const text = link
    const input = document.createElement('input')
    input.setAttribute('value', text)
    document.body.appendChild(input)
    input.select()
    document.execCommand('copy')
    document.body.removeChild(input)
    setBookmarksModalOpen(true)
  }

  const handleLoginModalValueChange = (value: string, mode: string): void => {
    if (mode === 'email') {
      setLoginModalConfig({
        ...loginModalConfig,
        value,
        email: value
      })
    } else if (mode === 'code') {
      setLoginModalConfig({
        ...loginModalConfig,
        value,
      })
    }
  }

  const closeLoginModalWindow = (action?: string): void => {
    const defaultConfig = {
      mode: 'email',
      value: '',
      methodId: '',
      email: '',
      loadingOTP: false,
      open: action === 'back',
      error: null
    }
    setLoginModalConfig({
      ...loginModalConfig,
      ...defaultConfig
    })
  }

  const attemptAdminMode = async (mode: string): Promise<void> => {
    // The OTP code input auto-submits via LoginModal's useEffect once 6 digits
    // are entered, and the "Sign In" button is still clickable at that point —
    // without this guard a concurrent second call can resolve after the first
    // one already closed the modal, and its stale closure re-opens it.
    if (loginModalConfig.loadingOTP) return
    try {
      if (mode === 'email') {
        setLoginModalConfig({
          ...loginModalConfig,
          loadingOTP: true,
          error: null
        })
        const res = await diamondStore.startOTP(loginModalConfig.email)

        if (res.success) {
          setLoginModalConfig({
            ...loginModalConfig,
            methodId: res.payload.method_id,
            mode: 'code',
            value: '',
            loadingOTP: false,
            error: null
          })
        } else {
          setLoginModalConfig({
            ...loginModalConfig,
            loadingOTP: false,
            error: res?.info?.error_message
          })
        }
      } else if (mode === 'code') {
        setLoginModalConfig({
          ...loginModalConfig,
          loadingOTP: true,
          error: null
        })
        const res = await diamondStore.validateOTP(loginModalConfig.methodId, loginModalConfig.value)
        if (res.success) {
          if (res.payload.merchant_token === undefined) {
            setLoginModalConfig({
              ...loginModalConfig,
              loadingOTP: false,
              error: serverErrorMessage(res?.payload) ?? null
            })
          } else {
            setIsMemoMode(true)
            const userRoles = res.payload.user.roles
            //TODO: remove roles length check
            const role =
              userRoles.includes(UserRoles.MerchantAdmin) ||
                userRoles.length === 0 ||
                res.payload.user.is_owner
                ? Roles.Merchant
                : Roles.Customer
            setRole(role)
            localStorage.setItem(USER_COOKIE_NAME, JSON.stringify(res.payload))
            setUserData(res.payload)
            closeLoginModalWindow()
            const url = new URL(window.location.href)
            url.searchParams.delete('admin')
            window.history.replaceState({}, '', url.toString())
            wishlistProps?.refresh(role, true)
          }
        } else {
          setLoginModalConfig({
            ...loginModalConfig,
            loadingOTP: false,
            error: serverErrorMessage(res?.payload) ?? null
          })
        }
      }
    } catch (e) {
      logger.info(e)
    }
  }

  const handleDASOrdering = (diamondsData: { [key: string]: any }, orderType: string): void => {
    const gems = Array.isArray(diamondsData) ? diamondsData : [diamondsData]
    setSelectedGems(gems)
    setSelectedGemsOrderType(orderType)
    setConfirmationModalOpen(true)
  }

  const handleAddToDAS = useCallback(async (): Promise<void> => {
    const result = await storeFunctions.addToDAS({
      diamonds: selectedGems,
      type: selectedGemsOrderType,
      shop: shopName || '',
      searchType: 'diamond_search'
    })
    if (!result.success) {
      setConfirmationModalOpen(false)
      setError({
        title: result.error?.errorTitle || '',
        message: result.error?.errorBody || ''
      })
      setErrorModalOpen(true)
      return
    }
    setConfirmationModalOpen(false)
    setDasOrderSuccessfullModalOpen(true)
  }, [selectedGems, selectedGemsOrderType, shopName])

  const handleAddToCart = useCallback(async (gems: { [key: string]: any }): Promise<void> => {
    setAddingSku(gems?.sku ?? null)
    const currentProductData = productDataRef.current
    let variantId = currentProductData.variantId
    if (variantId) {
      const currentVariant = currentProductData?.variants?.find(
        variant => variant.variant_id === currentProductData.variantId
      )
      const currentSelectedOptions = currentVariant?.selected_options
      const currentVariantId = findVariantIdByShapeChange(
        currentProductData.variants,
        currentSelectedOptions,
        gems.shape
      )
      if (currentVariantId) {
        variantId = currentVariantId
      }
    }
    try {
      const result = await storeFunctions.addToCart(
        [gems],
        ({ ...currentProductData, variantId }),
        'diamond_search'
      )
      if (!result.success) {
        setError({
          title: result.error?.errorTitle || '',
          message: result.error?.errorBody || ''
        })
        setErrorModalOpen(true)
        return
      }
      window.location.href = '/cart'
    } finally {
      setAddingSku(null)
    }
  }, [storeFunctions])

  const handleAddToCartRef = useRef(handleAddToCart)
  useEffect(() => {
    handleAddToCartRef.current = handleAddToCart
  }, [handleAddToCart])

  const SelectDiamondButtonWithHandler = useCallback(
    ({ data }: { data: { [key: string]: any } }) => (
      <StableSelectDiamondButton
        data={data}
        onSelect={handleAddToCartRef.current}
      />
    ),
    []
  )

  const { jType, isFullDiamondSearchJourney } = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search)
    const jType = searchParams.get('jewelry_type')
    const customerJourney = searchParams.get('customer_journey')
    const isFullDiamondSearchJourney = customerJourney === 'full_diamond_search'
    return { jType, customerJourney, isFullDiamondSearchJourney }
  }, [props.location.search])
  const mode =
    jType == 'ring'
      ? JewelryType.Ring
      : jType == 'pendant'
        ? JewelryType.Pendant
        : jType == 'bracelet'
          ? JewelryType.Bracelet
          : jType == 'earring'
            ? JewelryType.Earring
            : ''
  const activeStep = productData?.variantId ? 3 : 1;

  const diamondSearchEventProps = {
    api: {
      searchProducts: getDiamonds,
      countProducts: getDiamondsCount,
    },
    onParamsChange,
    onSelectedDiamondChange: ({ sku }: { sku: string }) =>
      onSelectedDiamondChange({ sku }),
    onEmailClick: bookmarksEmail,
    ...(!productData?.variantId && {
      onAddJewelryItem: (item: { [key: string]: any }): void => {
        const jewelryType = jType !== null ? `?jewelry_type=${jType}` : "";
        history.push(`/apps/ub/diamond_collection/${item.sku}${jewelryType}`);
      },
    }),

    ...(!(!config.advancedSearchCartEnabled && !productData?.variantId)
      ? { onAddCartItem: handleAddToCart }
      : {}),
    ...(isMemoMode
      ? {
          onAddInStoreItem: (item: { [key: string]: any }): void => handleDASOrdering(item, 'i'),
          onAddMemoItem: (item: { [key: string]: any }): void => handleDASOrdering(item, 'm')
        }
      : {}),
    onDetailsClick: (item: { [key: string]: any }, isCustomerLink: boolean): void => {
      let pathPart = 'merchant/diamond'
      if (
        (isCustomerLink && role === Roles.Merchant) ||
        (!isCustomerLink && role !== Roles.Merchant)
      ) {
        pathPart = 'diamond'
      }

      const params = new URLSearchParams(window.location.search)

      if (jType !== null) {
        params.set('jewelry_type', jType)
      }

      const query = params.toString()

      window.open(`${window.location.origin}/apps/ub/${pathPart}/${item.sku}${query ? `?${query}` : ''}`, '_blank')
    },
    onJewelryCheck: async (data: { [key: string]: any }): Promise<boolean> => {
      try {
        const productIds = await storeFunctions.tagProductsForSKU([data]);
        return !!productIds;
      } catch (e) {
        return false;
      }
    },
  }

  const diamondSearchProps: DiamondSearchProps = {
    ...diamondSearchEventProps,
    role,
    stickyHeaderTop: 0,
    ...(process.env.VIEWER_URL ? { viewerUrl: process.env.VIEWER_URL } : {}),
    merchant: { company_name: shopName || '', first_name: '' },
    isOnboarded: true,
    isActivated: true,
    getDefaultParams: useCallback(() => getDefaultParams(locationSearchRef.current), []),
    // Disabled to show price on store currency
    currencyProps: {
      mode: role === Roles.Merchant || isMemoMode ? 'double' : 'single',
      currencies: currencyInfo.currencies,
      currencyCode: currencyInfo.currency,
      // Second (customer-currency) line = retail_price x this rate. Use the Shopify
      // presentment rate so it matches checkout/other surfaces (not DAS's rate). In
      // single mode retail_price is already converted, so rate is 1 (no re-multiply).
      currencyRate:
        role === Roles.Merchant || isMemoMode
          ? shopCurrency && currencyInfo.currency !== shopCurrency
            ? window.Shopify?.currency?.rate || 1
            : 1
          : 1
    },
    disableFiltersWhenSKU: true,
    enabledDiamondGemTypes: enabledDiamondGemTypes,
    v360PlayerUrl,
    nativeV360PlayerUrl,
    labels: typeLabels,
    ringSizeProps: {
      sizeRegion: config.ringSizeRegion
    }
  }

  if (role === Roles.Merchant || isMemoMode) {
    diamondSearchProps.wishlistProps = wishlistProps
  }

  const productItemProps = useMemo(
    () => (isFullDiamondSearchJourney ? { button: SelectDiamondButtonWithHandler } : undefined),
    [isFullDiamondSearchJourney]
  )

  return (
    <div
      className={classNames(
        'unbridaled-search-page',
        role === Roles.Merchant && ' merchant-search-page'
      )}
    >
      <LoginModal
        loginModalConfig={{
          ...loginModalConfig,
          ...getLoginModalLabels(t, loginModalConfig.mode, {
            emailTitle: t('loginModal.signInWithEmail'),
            codeConfirm: t('loginModal.signIn')
          })
        }}
        onConfirm={attemptAdminMode}
        onClose={closeLoginModalWindow}
        onChange={handleLoginModalValueChange}
      />
      <ErrorModal
        open={errorModalOpen}
        title={error?.title}
        message={error?.message}
        onClose={closeErrorModal}
      />
      <BookmarksModal
        open={bookmarksModalOpen}
        shopName={shopName}
        favorites={wishlistProps?.products || []}
        config={config}
        onClose={closeBookmarksModal}
        role={role}
        isMemoMode={isMemoMode}
        stoneType="diamond"
      />
      <ConfirmationModal
        open={confirmationModalOpen}
        gems={selectedGems}
        orderType={selectedGemsOrderType}
        config={config}
        onConfirm={handleAddToDAS}
        onClose={closeConfirmationModal}
      />
      <DASOrderSuccessfullModal
        open={dasOrderSuccessfullModalOpen}
        gems={selectedGems}
        orderType={selectedGemsOrderType}
        config={config}
        onClose={closeDASOrderSuccessfullModal}
      />
      {loaded ? (
        <>
          <JourneyBar variantId={productData?.variantId} activeStep={activeStep} mode={mode} />
          <AddingSkuContext.Provider value={addingSku}>
            <DiamondsSearchPage
              key={`diamonds-search-${role}-${isMemoMode ? 'memo' : 'default'}`}
              locale={getSearchSdkLocale()}
              productItemProps={productItemProps}
              shareProps={{ hidden: true }}
              overrides={{
                ...(role === Roles.Customer && isMemoMode
                  ? { filtersMode: Roles.Merchant }
                  : {}),
                dataView: {
                  headerActions:
                    role === Roles.Merchant ? (
                      <Menu className="flex" placement="bottom-start">
                        <MenuButton
                          as={Button}
                          className={classNames(
                            'uui-flex uui-items-center uui-h-fit uui-h-[30px]',
                            isMobile && 'uui-px-0'
                          )}
                          disabled={false}
                          size={'small'}
                          text={!isMobile ? 'Exit' : ''}
                          RightIcon={
                            !isMobile && (
                              <ArrowHeadDownIcon
                                size="14px"
                                color="uui-text-white"
                                className="uui-hidden sm:uui-block"
                              />
                            )
                          }
                        >
                          {isMobile && (
                            <LogoutIcon className="uui-mr-1.5 uui-ml-1.5" color="uui-text-white" />
                          )}
                        </MenuButton>
                        <MenuItems>
                          <MenuLabel
                            title={t('common.memoMode', 'Memo Mode')}
                            variant="default"
                            onClick={onSwitchToMemoMode}
                            disabled={false}
                          />
                          <MenuLabel
                            title={t('common.logout', 'Logout')}
                            variant="default"
                            onClick={onLogout}
                            disabled={false}
                          />
                        </MenuItems>
                      </Menu>
                    ) : isMemoMode ? (
                      <Button
                        text={!isMobile ? 'Exit' : ''}
                        className={classNames(
                          'uui-flex uui-items-center uui-h-fit uui-h-[30px]',
                          isMobile && 'uui-px-0'
                        )}
                        onClick={onLogout}
                        disabled={false}
                      >
                        {isMobile && (
                          <LogoutIcon className="uui-mr-1.5 uui-ml-1.5" color="uui-text-white" />
                        )}
                      </Button>
                    ) : undefined
                },
                ...overrides
              }}
              {...diamondSearchProps}
            />
          </AddingSkuContext.Provider>
        </>
      ) : (
        <Preloader preloaderClass="unbridaled-full-page-preloader" />
      )}
    </div>
  )
}

export default DiamondsMainSearchPage
